import { cn } from '@/lib/utils'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useState } from 'react'

interface Props {
  days: number
  maxDays: number
  selected: boolean
  onApply: (days: number) => void
}

export const LastDaysQuickRange: React.FC<Props> = ({
  days,
  maxDays,
  selected,
  onApply,
}) => {
  const translations = useTranslate({
    prefix: 'components.date-picker-quick-ranges.last-days-prefix',
    suffix: 'components.date-picker-quick-ranges.last-days-suffix',
    input: 'components.date-picker-quick-ranges.last-days-input',
  })

  const [draft, setDraft] = useState(String(days))
  const [syncedDays, setSyncedDays] = useState(days)
  if (syncedDays !== days) {
    setSyncedDays(days)
    setDraft(String(days))
  }

  function parseDraft() {
    const parsed = Number(draft)
    if (!Number.isInteger(parsed) || parsed < 1) return null
    return Math.min(parsed, maxDays)
  }

  function apply() {
    const next = parseDraft() ?? days
    setDraft(String(next))
    onApply(next)
  }

  function handleBlur() {
    const next = parseDraft()
    if (next === null) setDraft(String(days))
    else if (next !== days) apply()
  }

  return (
    <li className="col-span-2 md:not-first:mt-0.5">
      <div
        className={cn(
          'flex w-full items-center gap-1.5 py-1 px-4 rounded-lg text-stone-800',
          !selected && 'hover:bg-primary-hover',
          selected && 'bg-primary text-white'
        )}
      >
        <button type="button" className="cursor-pointer" onClick={apply}>
          {translations.prefix}
        </button>
        <input
          type="number"
          inputMode="numeric"
          min={1}
          max={maxDays}
          value={draft}
          aria-label={translations.input}
          className="w-14 rounded-md border border-gray-200 bg-white px-1 text-center text-stone-800 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          onChange={e => setDraft(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={e => {
            if (e.key !== 'Enter') return
            e.preventDefault()
            apply()
          }}
        />
        <button
          type="button"
          tabIndex={-1}
          className="cursor-pointer"
          onClick={apply}
        >
          {translations.suffix}
        </button>
      </div>
    </li>
  )
}
